import {
  inject,
  OnInit,
  Component,
  OnDestroy,
  AfterViewInit,
} from "@angular/core";
import {
  getValue,
  RemoteConfig,
  fetchAndActivate,
} from "@angular/fire/remote-config";
import {
  ADD_LANGUAGES,
  ADD_CATEGORIES,
  ADD_COST_CENTERS,
  ADD_SITE_SEGMENTS,
  ADD_NATIONAL_EMERGENCY,
} from "./store/actions/master.action";
import { of } from "rxjs";
import { Store } from "@ngrx/store";
import { App } from "@capacitor/app";
import { Router } from "@angular/router";
import { Capacitor } from "@capacitor/core";
import { catchError } from "rxjs/operators";
import { Browser } from "@capacitor/browser";
import { Keyboard } from "@capacitor/keyboard";
import { registerAppIcons } from "./app.icons";
import { StatusBar } from "@capacitor/status-bar";
import { HttpErrorResponse } from "@angular/common/http";
import { from, Subject, Subscription, timer } from "rxjs";
import { differenceInMilliseconds, isBefore } from "date-fns";
import { ADD_CURRENCY } from "./store/actions/currency.action";
import { ScreenOrientation } from "@capacitor/screen-orientation";
import { UserService } from "./services/user-service/user.service";
import { ToastService } from "./services/toast-service/toast.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { AppConfig, TermsConfig } from "./store/models/appConfig.model";
import { HttpService } from "./services/http-service/http-client.service";
import { StorageService } from "./services/storage-service/storage.service";
import { AlertController, IonApp, Platform } from "@ionic/angular/standalone";
import { exhaustMap, filter, map, switchMap, takeUntil } from "rxjs/operators";
import { JailbreakRoot } from "@basecom-gmbh/capacitor-jailbreak-root-detection";
import {
  SyncApppDetailsType,
  SyncApppDetailsResponseModel,
} from "./store/models/syncApppDetails.model";
import { TabsMenuComponent } from "./components/tabs-menu-component/tabs-menu.component";
import { NotificationService } from "./services/notification-service/notification.service";
import { SsoAuthService } from "./services/sso-auth-service/sso-auth.service";
import { FirebaseRealtimeService } from "./services/firebase-realtime-service/firebase-realtime.service";
import { AssetModel, SiteModel } from "./store/models/asset.model";
import { UserModel } from "./store/models/user.model";

interface Tab {
  id: string;
  title: string;
  route: string;
  activeIcon: string;
  hasAccess?: boolean;
  inactiveIcon: string;
}

@Component({
  standalone: true,
  selector: "app-root",
  templateUrl: "app.component.html",
  imports: [TranslateModule, IonApp, TabsMenuComponent],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private store = inject(Store);

  private router = inject(Router);
  private platform = inject(Platform);
  private storage = inject(StorageService);
  private userService = inject(UserService);
  private httpService = inject(HttpService);
  private altCtrl = inject(AlertController);
  private toastService = inject(ToastService);
  private remoteConfig = inject(RemoteConfig);
  private translate = inject(TranslateService);
  private notificationService = inject(NotificationService);
  private ssoAuthService = inject(SsoAuthService);
  private fbrealtimeService = inject(FirebaseRealtimeService);

  private countryId: string;
  private lastUpdateTime: Date;
  private lastPopUpTime: number;
  private inAppBrowser = Browser;
  private skipUpdateCount: number;
  private observer: MutationObserver;

  private realTimeDBsubscription: Subscription;
  private _autoSyncDestroy$ = new Subject<void>();

  constructor() {
    registerAppIcons();
    this.lastPopUpTime = 0;
    this.skipUpdateCount = 0;
    this.lastUpdateTime = new Date();
  }

  ngOnInit(): void {
    // Also completes the web Google sign-in popup redirect
    this.ssoAuthService.initialize().catch(() => {});

    this.platform.ready().then(async () => {
      const isDevice = Capacitor.isNativePlatform();

      if (isDevice) {
        const isRooted = await this.RootedDevice();
        if (isRooted) return; // Block app init if rooted

        await StatusBar.hide();
        this.ScreenLockOrientation();
        Keyboard.setAccessoryBarVisible({ isVisible: false });

        Keyboard.addListener("keyboardDidShow", () => {
          const active = document.activeElement as HTMLElement;
          if (
            active &&
            (active.tagName === "INPUT" || active.tagName === "TEXTAREA")
          ) {
            setTimeout(() => {
              active.scrollIntoView({ block: "center", behavior: "smooth" });
            }, 50);
          }
        });

        this.InitializeApp();
        this.CheckAppUpgrade();

        this.notificationService.initializeNotification();
      } else {
        this.InitializeApp();
        this.CheckAppUpgrade();
      }
    });
  }

  async InitializeApp() {
    const user = await this.storage.get("user");

    if (!user && !user?.email) {
      this.router.navigate(["/login"]);
    }

    await this.httpService.initTenantInfo();
    this.countryId =
      user?.checkedInLocation && user?.checkedInLocation?.site
        ? user?.checkedInLocation?.country?.locationId
        : user?.defaultLocation?.country?.locationId;

    await this.userService.initializeUser(user);
    this.userService.navigateToDefaultTab(user);

    const storage = [
      "languages",
      "categories",
      "costCenters",
      "siteSegments",
      "localCurrency",
      "selectedLanguage",
      "nationalEmergencies",
    ];

    const [
      languages,
      categories,
      costCenters,
      siteSegments,
      localCurrency,
      selectedLanguage,
      nationalEmergencies,
    ] = await Promise.all(storage.map((key) => this.storage.get(key)));

    const dispatchMap = [
      { data: languages, action: ADD_LANGUAGES },
      { data: categories, action: ADD_CATEGORIES },
      { data: localCurrency, action: ADD_CURRENCY },
      { data: costCenters, action: ADD_COST_CENTERS },
      { data: siteSegments, action: ADD_SITE_SEGMENTS },
      { data: nationalEmergencies, action: ADD_NATIONAL_EMERGENCY },
    ];

    dispatchMap.forEach(({ data, action }) => {
      if (data) this.store.dispatch(action(data));
    });

    this.initAutoSyncListener();
    this.initAppForegroundListener();
  }
  private initAppForegroundListener(): void {
    App.addListener("appStateChange", async ({ isActive }) => {
      if (!isActive) return;

      await this.fbrealtimeService.forceReconnect();
      this.initAutoSyncListener();
    });
  }

  private initAutoSyncListener(): void {
    this._autoSyncDestroy$.next();
    this._autoSyncDestroy$.complete();
    this._autoSyncDestroy$ = new Subject<void>();

    this.realTimeDBsubscription = this.fbrealtimeService
      .getLastUpdatedTimestamp()
      .pipe(
        takeUntil(this._autoSyncDestroy$),

        filter((data) => !!data?.last_updated),

        exhaustMap((data) =>
          from(this.storage.get("last_updated")).pipe(
            map((lastUpdatedFromStorage) => {
              const lastUpdated = data.last_updated;


              if (!lastUpdatedFromStorage) return lastUpdated;

              return lastUpdatedFromStorage;
            }),

            switchMap((lastUpdated) =>
              from(this.storage.get("syncDelayTimeInMinutes")).pipe(
                map((syncDelayTimeMinute) => syncDelayTimeMinute || 5),

                switchMap((delayTime) =>
                  timer(60).pipe(
                    switchMap(() =>
                      from(this.storage.get("offline_sync_completed")),
                    ),

                    switchMap((isSyncCompleted) => {

                      if (isSyncCompleted === "true") {
                        return from(
                          this.syncLatestUpdatesWithServer(lastUpdated),
                        );
                      }

                      return of(null);
                    }),
                  ),
                ),
              ),
            ),
          ),
        ),

        catchError((err) => {
          console.error("[AppComponent] Error during auto sync:", err);
          return of(null);
        }),
      )
      .subscribe();
  }

  private async syncLatestUpdatesWithServer(
    lastUpdated: string,
  ): Promise<void> {
    this.httpService.syncApppDetails(lastUpdated).subscribe({
      next: async (response: SyncApppDetailsResponseModel) => {
        if (response.status && response.data) {
          await this.updateSiteInLocalStorage(response.data);
          await this.updateUserInLocalStorage(response.data);
          await this.updateAssetInLocalStorage(response.data);
          if (!this.countryId) {
            const user = await this.storage.get("user");
            this.countryId =
              user?.checkedInLocation && user?.checkedInLocation?.site
                ? user?.checkedInLocation?.country?.locationId
                : user?.defaultLocation?.country?.locationId;
          }
          await this.CountryAssessmentDownload(this.countryId);
        }
      },
      error: (error: HttpErrorResponse) => {
      },
      complete: () => {
        this.storage.set("offline_sync_completed", "true");
        this.storage.set("syncDelayTimeInMinutes", 5);
        this.storage.set("last_updated", new Date().toISOString());
      },
    });
  }

  async CheckAppUpgrade(): Promise<void> {
    const isDevice = Capacitor.isNativePlatform();

    // Fetch all remote config and activate them
    await fetchAndActivate(this.remoteConfig);
    const remoteConfig = getValue(this.remoteConfig, "appVersionConfig");
    const appConfig = JSON.parse(remoteConfig.asString()) as AppConfig;
    await this.storage.set("appConfig", appConfig);

    if (isDevice) {
      const appVersion = (await App.getInfo()).version;
      this.lastUpdateTime = new Date(appConfig.force_update_date);
      const timeoutInterval = appConfig.pop_up_duration * 60 * 60 * 1000; // 8 hours in ms
      this.lastPopUpTime = (await this.storage.get("lastPopUpTime")) || 0;

      const timeOutPassed = this.lastPopUpTime
        ? differenceInMilliseconds(new Date(), this.lastPopUpTime) >=
          timeoutInterval
        : true;

      // Compare versions
      if (this.compareVersions(appVersion, appConfig.app_version) < 0) {
        // Only show when force_update = true
        if (appConfig.force_update) {
          // If user clicked Later before → wait 8 hrs
          if (
            timeOutPassed ||
            isBefore(new Date(), new Date(this.lastUpdateTime))
          ) {
            this.showUpdateAlert(appConfig);
          }
        }
      } else {
        this.storage.remove("lastPopUpTime");
        this.storage.remove("skipUpdateCount");
      }
    }

    // Terms config
    const termsConfig = getValue(this.remoteConfig, "terms_and_conditions");
    const termsAppConfig = JSON.parse(termsConfig.asString()) as TermsConfig;
    await this.storage.set("terms_and_conditions", termsAppConfig);
  }

  async showUpdateAlert(config: AppConfig) {
    let btns = [];
    if (!config.force_update) return;

    if (isBefore(new Date(), new Date(this.lastUpdateTime))) {
      btns.push({
        text: "Later\n(after 8 hours)",
        handler: () => this.skipForceUpdate(config), // Save timestamp only here
        cssClass: "btn-later two-line no-uppercase",
      });
    } else {
      btns.push({
        text: "Later\n(after 8 hours)",
        cssClass: "btn-inactive two-line no-uppercase",
      });
    }

    btns.push({
      text: "Update\nnow",
      handler: () => this.openMarketPage(config),
      cssClass: "btn-active two-line no-uppercase",
    });

    let alert = await this.altCtrl.create({
      buttons: btns,
      backdropDismiss: false,
      cssClass: "version-alert",
      header: config.alert_title,
      message: config.alert_message,
    });

    await alert.present();
  }

  skipForceUpdate(config: AppConfig) {
    this.storage.set("lastPopUpTime", Date.now()); // only store when "Later" clicked
    this.storage.set("skipUpdateCount", this.skipUpdateCount + 1);
  }

  openMarketPage(config: AppConfig): boolean {
    if (this.platform.is("android")) {
      if (config.force_update_android_store_url) {
        this.inAppBrowser.open({
          windowName: "_system",
          url: config.force_update_android_store_url,
        });
      }
      return true;
    } else if (this.platform.is("ios")) {
      if (config.force_update_iOS_store_url) {
        this.inAppBrowser.open({
          windowName: "_system",
          url: config.force_update_iOS_store_url,
        });
      }
      return true;
    }

    return false;
  }

  compareVersions(v1: string, v2: string): number {
    const parts1 = v1?.split(".")?.map(Number);
    const parts2 = v2?.split(".")?.map(Number);

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const a = parts1[i] ?? 0;
      const b = parts2[i] ?? 0;
      if (a !== b) return a - b;
    }
    return 0;
  }

  async RootedDevice(): Promise<boolean> {
    const rooted = await JailbreakRoot.isJailbrokenOrRooted();
    if (rooted.result) {
      let alert = await this.altCtrl.create({
        mode: "ios",
        backdropDismiss: false,
        cssClass: "alertButton",
        message: this.translate.instant("ROOTED_DEVICE_NOT_ALLOWED"),
        buttons: [
          {
            role: "cancel",
            text: this.translate.instant("EXIT_APP"),
            cssClass: "exitButton",
            handler: () => App.exitApp(),
          },
        ],
      });
      alert.present();
      return true;
    }

    return false;
  }

  async ScreenLockOrientation() {
    await ScreenOrientation.lock({ orientation: "landscape-primary" });
  }

  ngAfterViewInit(): void {
    const disableImageDragging = (root: HTMLElement | Document = document) => {
      // Disable dragging for <img> tags
      const imgs = root.querySelectorAll("img");
      imgs.forEach((img) => img.setAttribute("draggable", "false"));

      // Disable dragging for <ion-img> tags
      const ionImgs = root.querySelectorAll("ion-img");
      ionImgs.forEach((ionImg) => ionImg.setAttribute("draggable", "false"));
    };

    // Initial run
    disableImageDragging();

    // Watch for added nodes and apply again
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            disableImageDragging(node);
          }
        });
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  CountryAssessmentDownload(countryId: string): Promise<void> {
    return new Promise<void>((resolve) => {
      this.httpService.getAssessmentsForCountriesSite({ countryId }).subscribe({
        next: async (downloadedAssessments) => {
          try {
            const siteWiseAssessments = downloadedAssessments.data;
            await this.storage.set(
              "countryWiseSiteAssessments",
              siteWiseAssessments,
            );
            resolve();
          } catch (err) {
            this.toastService.toastFailed("Failed to save data locally.");
            resolve();
          }
        },
        error: (err) => {
          this.toastService.toastFailed(
            "Failed to sync data. Please try again.",
          );
          resolve();
        },
      });
    });
  }

  ngOnDestroy(): void {
    if (this.observer) this.observer.disconnect();

    // Clean up Realtime DB subscription on component destroy
    if (this.realTimeDBsubscription) {
      this.realTimeDBsubscription.unsubscribe();
    }

    // Clean up Realtime DB subscription on component destroy
    this._autoSyncDestroy$.next();
    this._autoSyncDestroy$.complete();
  }

  async updateSiteInLocalStorage(syncData: SyncApppDetailsType) {

    const sitesData = syncData["sites"] as SiteModel[];

    if (sitesData?.length) {
      const allCache = (await this.storage.get("allCache")) || [];

      if (allCache.length) {
        const cacheData = allCache[0];

        const sites = cacheData.sites || [];

        for (const updatedSite of sitesData) {
          const existingSiteIndex = sites.findIndex(
            (site: SiteModel) => site.id === updatedSite.id,
          );

          if (existingSiteIndex !== -1) {
            sites[existingSiteIndex] = updatedSite;
          } else {
            sites.push(updatedSite);
          }
        }

        cacheData.sites = sites;

        await this.storage.set("allCache", allCache);
      }
    }
  }
  async updateUserInLocalStorage(syncData: SyncApppDetailsType) {
    const usersData = syncData["users"] as UserModel[];

    if (usersData?.length) {
      let usersCache = (await this.storage.get("usersCache")) || {};

      for (const updatedUser of usersData) {
        const countryId = updatedUser?.defaultLocation?.country?.locationId;

        if (!countryId) continue;

        const existingUsers = usersCache[countryId] || [];

        const existingUserIndex = existingUsers.findIndex(
          (user: UserModel) => user.id === updatedUser.id,
        );

        if (existingUserIndex !== -1) {
          existingUsers[existingUserIndex] = updatedUser;
        } else {
          existingUsers.push(updatedUser);
        }

        usersCache[countryId] = existingUsers;
      }

      await this.storage.set("usersCache", usersCache);
    }
  }
  async updateAssetInLocalStorage(syncData: SyncApppDetailsType) {
    const assetsData = syncData["assets"] as AssetModel[];

    if (!assetsData?.length) return;

    const allCache = (await this.storage.get("allCache")) || [];

    if (!allCache.length) return;

    const cacheData = allCache[0];

    const existingAssets: AssetModel[] = cacheData.assets || [];

    for (const updatedAsset of assetsData) {
      const existingAssetIndex = existingAssets.findIndex(
        (asset) => asset.id === updatedAsset.id,
      );

      if (existingAssetIndex !== -1) {
        existingAssets[existingAssetIndex] = updatedAsset;
      } else {
        existingAssets.push(updatedAsset);
      }
    }

    cacheData.assets = existingAssets;
    allCache[0] = cacheData;

    await this.storage.set("allCache", allCache);
  }
}
